import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  IListDto,
  IListResponseData,
  INgxsFormModel,
  IPartnerCodeDetails,
  IPartnerCodesList,
} from '@pro-connect/shared';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { IDiscountCode } from './models/discount-codes';
import { IMyDataFormModel } from './models/my-data-form-model';
import { IMyDataList } from './models/my-data-list-model';
import { MyDataService } from './my-data.service';
import { IAccountChangeAgreementsDto } from '../../models/account-change-agreements-dto';
import { IOrder } from './models/order';

export class MyDataStateChangeClientAgreement {
  public static readonly type = '[MyDataState] change client agreement';

  public constructor(public readonly payload: IAccountChangeAgreementsDto) {}
}

export class MyDataStateGenerateDiscountCode {
  public static readonly type = '[MyDataState] generate a discount code for a logged client';
}

export class MyDataStateGetClientDiscountCodes {
  public static readonly type = '[MyDataState] get a list of client discount codes';

  public constructor(public readonly payload: IListDto) {}
}

export class MyDataStateGetListOfPartnerCodes {
  public static readonly type = '[MyDataState] get a list of partner codes';
}

export class MyDataStateGetPartnerCodeDetails {
  public static readonly type = '[MyDataState] get details of partner code';

  public constructor(public readonly payload: { id: number }) {}
}

export class MyDataStateGetClientOrders {
  public static readonly type = '[MyDataState] get a list of client orders';

  public constructor(public readonly payload?: { listDto: IListDto }) {}
}

export interface MyDataStateModel {
  loading: boolean;
  form: INgxsFormModel<IMyDataFormModel>;
  discountCodes: IMyDataList<IDiscountCode>;
  paymentHistory: IMyDataList<IOrder>;
  partnerCodes: IPartnerCodesList[];
  partnerCodeDetails: {
    [id: number]: IPartnerCodeDetails;
  };
}

@State<MyDataStateModel>({
  name: 'myData',
  defaults: {
    loading: false,
    discountCodes: {
      list: null,
      listDto: {
        direction: 'desc',
        page: 1,
        perPage: 9999,
        sort: 'created_at',
      },
    },
    paymentHistory: {
      list: null,
      listDto: {
        direction: 'desc',
        page: 1,
        perPage: 10,
        sort: 'created_at',
      },
    },
    form: {
      model: {
        name: null,
        nip: null,
        street: null,
        zipCode: null,
        city: null,
        firstname: null,
        lastname: null,
        mobile: null,
        email: null,
      },
      errors: {},
      dirty: false,
      disabled: false,
      status: 'pristine',
    },
    partnerCodes: [],
    partnerCodeDetails: {},
  },
})
@Injectable()
export class MyDataState {
  public constructor(private readonly _myDataService: MyDataService) {}

  @Selector()
  public static loading({ loading }: MyDataStateModel): boolean {
    return loading;
  }

  @Selector()
  public static discountCodes({ discountCodes }: MyDataStateModel): IListResponseData<IDiscountCode> | null {
    return discountCodes.list;
  }

  @Selector()
  public static paymentHistory({ paymentHistory }: MyDataStateModel): IListResponseData<IOrder> | null {
    return paymentHistory.list;
  }

  @Selector()
  public static partnerCodes({ partnerCodes }: MyDataStateModel): IPartnerCodesList[] {
    return partnerCodes;
  }

  @Selector()
  public static partnerCodeDetails({ partnerCodeDetails }: MyDataStateModel): {
    [id: number]: IPartnerCodeDetails;
  } {
    return partnerCodeDetails;
  }

  @Action(MyDataStateChangeClientAgreement)
  public changeClientAgreement(
    { patchState }: StateContext<MyDataStateModel>,
    { payload }: MyDataStateChangeClientAgreement,
  ) {
    patchState({ loading: true });
    return this._myDataService.changeClientAgreement(payload).pipe(
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(MyDataStateGenerateDiscountCode)
  public generateDiscountCode({ patchState, dispatch, getState }: StateContext<MyDataStateModel>) {
    patchState({ loading: true });
    return this._myDataService.generateDiscountCode().pipe(
      switchMap(() => {
        const { listDto } = getState().discountCodes;
        return dispatch([new MyDataStateGetClientDiscountCodes(listDto)]);
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(MyDataStateGetClientDiscountCodes)
  public getDiscountCodeList(
    { patchState, getState }: StateContext<MyDataStateModel>,
    { payload }: MyDataStateGetClientDiscountCodes,
  ) {
    patchState({ loading: true });
    return this._myDataService.getClientDiscountCodes(payload).pipe(
      tap((list: IListResponseData<IDiscountCode>) => {
        const { listDto } = getState().discountCodes;

        patchState({
          discountCodes: {
            list: {
              ...list,
            },
            listDto,
          },
        });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(MyDataStateGetClientOrders)
  public getClientOrders(
    { patchState, getState }: StateContext<MyDataStateModel>,
    { payload }: MyDataStateGetClientOrders,
  ) {
    patchState({ loading: true });
    const { listDto } = getState().paymentHistory;

    return this._myDataService.getClientOrders(payload?.listDto ? payload.listDto : listDto).pipe(
      tap((list: IListResponseData<IOrder>) => {
        patchState({
          paymentHistory: {
            list: {
              ...list,
            },
            listDto: payload?.listDto ? payload.listDto : listDto,
          },
        });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(MyDataStateGetListOfPartnerCodes)
  public getPartnerCodes({ patchState }: StateContext<MyDataStateModel>) {
    patchState({ loading: true });
    return this._myDataService.getPartnerCodes().pipe(
      tap((partnerCodes) => {
        patchState({
          partnerCodes,
        });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(MyDataStateGetPartnerCodeDetails)
  public getPartnerCodeDetails(
    { patchState, getState }: StateContext<MyDataStateModel>,
    { payload: { id } }: MyDataStateGetPartnerCodeDetails,
  ) {
    patchState({ loading: true });
    return this._myDataService.getPartnerCodeDetails(id).pipe(
      tap((details) => {
        patchState({
          partnerCodeDetails: {
            ...getState().partnerCodeDetails,
            [id]: details,
          },
        });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }
}
